<script setup lang="ts">
import type { ContentArticle } from '~/models/Content/ContentArticle'
import { useRouter } from 'vue-router'
import { computed } from 'vue'
import { DropdownMenuContent, DropdownMenuItem, DropdownMenuRoot, DropdownMenuTrigger } from 'reka-ui'
import { storeToRefs } from 'pinia'
import { useFullscreen } from '@vueuse/core'
import { faSquareChevronDown } from '@fortawesome/duotone-regular-svg-icons'
import useAuthStore from '~/stores/auth'
import { ColorTheme } from '~/models/Content/Color'
import useSlugify from '~/composables/useSlugify'
import { useContentHelper } from '~/composables/useContentHelper'
import { useColorTheme } from '~/composables/useColorTheme'
import Tooltip from '~/components/ui/Tooltip.vue'
import SubArticleButton from '~/components/article/SubArticleButton.vue'

const { article, subArticles, routeParams } = defineProps<{
  article: ContentArticle
  subArticles: Array<ContentArticle>
  routeParams: Record<string | number, string | number>
}>()

const router = useRouter()
const { slugify } = useSlugify()
const { getTitle } = useContentHelper()
const { isFullscreen } = useFullscreen()
const { isTeacher } = storeToRefs(useAuthStore())

const themeIdentifier = computed(() => article.colorTheme?.identifier ?? ColorTheme.BlueOrange)
const { theme } = useColorTheme(themeIdentifier)

const subArticleIndex = computed(() => subArticles
  ?.findIndex(({ locationId }) => locationId === article.locationId) ?? 0)

const subRouteParams = (item: ContentArticle) => {
  let path = router.resolve({
    name: 'article_sub',
    params: { ...routeParams, ...{
      subLocationId: item.locationId,
      subSlug: slugify(item.title)
    } },
  }).href
  if (router.currentRoute.value.params?.listId) {
    path = router.resolve({
      name: 'favorite_list_navigation',
      params: { listId: router.currentRoute.value.params.listId }
    }).href + path
  }
  return path
}
</script>

<template>
  <div
    v-if="subArticles && subArticles.length > 0"
    class="z-1 col-start-1 row-start-1 mt-[20px] self-start md:mt-[22px]"
    :class="isFullscreen || !isTeacher ? 'ml-5' : 'ml-20'"
    data-ignore-tts
  >
    <div class="md:hidden">
      <DropdownMenuRoot>
        <DropdownMenuTrigger as-child>
          <SubArticleButton
            :selected="true"
            :theme
            :index="subArticleIndex"
            :icon="faSquareChevronDown"
            class="rounded"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          class="z-10 min-w-[100px] rounded bg-transparent p-1 text-xl"
          :side-offset="5"
        >
          <DropdownMenuItem
            v-for="(subArticle, index) in subArticles"
            :key="`sub-mobile-${subArticle.locationId}`"
            as-child
          >
            <SubArticleButton
              :selected="subArticle.locationId === article.locationId"
              :theme
              :index
              class="flex w-100 items-center justify-center py-4 text-xl [&:not(:last-child)]:border-b-0"
              @click.prevent="router.push(subRouteParams(subArticle))"
            />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuRoot>
    </div>
    <div class="hidden flex-row gap-2 md:flex">
      <Tooltip
        v-for="(subArticle, index) in subArticles"
        :key="`sub-${subArticle.locationId}`"
        :content="getTitle(subArticle)"
        side="bottom"
      >
        <SubArticleButton
          :selected="subArticle.locationId === article.locationId"
          :theme
          :index
          class="rounded"
          @click.prevent="router.push(subRouteParams(subArticle))"
        />
      </Tooltip>
    </div>
  </div>
</template>
